import React, { useState, useMemo, useRef } from 'react';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { Divider } from 'primereact/divider';

import { useGetAllBrandsQuery, useUpdateBrandMutation } from '../../../api/endpoints/brand';
import { useLanguage } from '../../../contexts/LanguageContext';

// Yalnızca marka alanını dropdown yapıyoruz
import BrandDropdown from '../../../components/common/BrandDropdown';

const BrandPage = () => {
    const { th } = useLanguage();
    const toast = useRef(null);

    const [lazyParams, setLazyParams] = useState({
        first: 0,
        rows: 10,
        page: 1,
        sortField: null,
        sortOrder: null
    });

    // API'ye giden aktif filtreler
    const [activeFilters, setActiveFilters] = useState({
        brandName: '',
        brandShortName: '',
        brandItemValue: ''
    });

    // Gelişmiş filtre paneli state
    const [filterOpen, setFilterOpen] = useState(false);
    const [brandSelect, setBrandSelect] = useState(null); // BrandDropdown seçimi (obj)
    const [brandShortText, setBrandShortText] = useState(''); // text
    const [brandItemValText, setBrandItemValText] = useState(''); // text

    // Dialog (güncelleme) state'leri
    const [dialogVisible, setDialogVisible] = useState(false);
    const [currentBrand, setCurrentBrand] = useState(null);
    const [originalBrand, setOriginalBrand] = useState(null);
    const [brandTitle, setBrandTitle] = useState('');
    const [brandCode, setBrandCode] = useState('');
    const [brandItemVal, setBrandItemVal] = useState('');
    const [aktif, setAktif] = useState(true);
    const [submitted, setSubmitted] = useState(false);

    const {
        data: brandsResponse,
        isLoading: loading,
        refetch
    } = useGetAllBrandsQuery(
        {
            pageNumber: lazyParams.page - 1,
            pageSize: lazyParams.rows,
            brandName: activeFilters.brandName || undefined,
            brandShortName: activeFilters.brandShortName || undefined,
            brandItemValue: activeFilters.brandItemValue || undefined
        },
        { refetchOnMountOrArgChange: true }
    );

    const [updateBrand] = useUpdateBrandMutation();

    const brands = brandsResponse?.data?.items || [];
    const totalRecords = brandsResponse?.data?.totalCount || 0;

    const sortedData = useMemo(() => {
        if (!lazyParams.sortField) return brands;
        return [...brands].sort((a, b) => {
            const aVal = a[lazyParams.sortField] || '';
            const bVal = b[lazyParams.sortField] || '';
            const order = lazyParams.sortOrder === 1 ? 1 : -1;
            return order * aVal.toString().localeCompare(bVal.toString(), 'tr', { numeric: true });
        });
    }, [brands, lazyParams.sortField, lazyParams.sortOrder]);

    const onPage = (event) => {
        const calculatedPage = Math.floor(event.first / event.rows) + 1;
        setLazyParams((prev) => ({
            ...prev,
            first: event.first,
            rows: event.rows,
            page: calculatedPage
        }));
    };

    const onSort = (event) => {
        setLazyParams((prev) => ({
            ...prev,
            sortField: event.sortField,
            sortOrder: event.sortOrder
        }));
    };

    const toggleFilter = () => setFilterOpen((v) => !v);

    const handleResetFilters = () => {
        setBrandSelect(null);
        setBrandShortText('');
        setBrandItemValText('');

        setActiveFilters({
            brandName: '',
            brandShortName: '',
            brandItemValue: ''
        });

        setLazyParams((prev) => ({
            ...prev,
            first: 0,
            page: 1,
            sortField: null,
            sortOrder: null
        }));
    };

    const handleApplyFilters = () => {
        const next = {
            brandName: (brandSelect?.brandShort || '').toUpperCase(),
            brandShortName: (brandShortText || '').toUpperCase(),
            brandItemValue: (brandItemValText || '').toUpperCase()
        };

        setActiveFilters(next);
        setLazyParams((prev) => ({ ...prev, first: 0, page: 1 }));
    };

    const hasChanges = () => {
        if (!originalBrand) return false;
        return brandTitle.trim() !== (originalBrand.brand || '').trim() || brandCode.trim() !== (originalBrand.brandShort || '').trim() || brandItemVal.trim() !== (originalBrand.brandItemVal || '').trim();
    };

    const handleEdit = (brand) => {
        setCurrentBrand(brand);
        setOriginalBrand(brand);
        setBrandTitle(brand.brand || '');
        setBrandCode(brand.brandShort || '');
        setBrandItemVal(brand.brandItemVal || '');
        setAktif(brand.aktif !== false);
        setSubmitted(false);
        setDialogVisible(true);
    };

    const hideDialog = () => {
        setDialogVisible(false);
        setSubmitted(false);
        setOriginalBrand(null);
    };

    const saveChanges = async () => {
        setSubmitted(true);
        if (!brandTitle.trim() || !brandCode.trim()) return;

        if (!hasChanges()) {
            if (toast.current) {
                toast.current.show({
                    severity: 'warn',
                    summary: th('common.warning'),
                    detail: th('brand.noChangesWarning'),
                    life: 3000
                });
            }
            return;
        }

        try {
            const brandData = {
                id: currentBrand?.id || 0,
                brand: brandTitle.trim(),
                brandShort: brandCode.trim(),
                brandItemVal: brandItemVal.trim() || ''
            };

            await updateBrand(brandData).unwrap();

            if (toast.current) {
                toast.current.show({
                    severity: 'success',
                    summary: th('common.success'),
                    detail: th('brand.updateSuccess'),
                    life: 3000
                });
            }

            hideDialog();
            refetch();
        } catch (error) {
            console.error('Error saving brand:', error);
            if (toast.current) {
                toast.current.show({
                    severity: 'error',
                    summary: th('common.error'),
                    detail: th('brand.updateError'),
                    life: 3000
                });
            }
        }
    };

    const renderDialogFooter = () => (
        <div className="flex justify-content-end gap-2">
            <Button label={th('common.cancel')} icon="pi pi-times" outlined onClick={hideDialog} type="button" />
            <Button label={th('brand.update')} icon="pi pi-check" onClick={saveChanges} type="button" />
        </div>
    );

    const actionBodyTemplate = (rowData) => (
        <div className="flex gap-2 justify-content-center">
            <i onClick={() => handleEdit(rowData)} className="pi pi-pencil cursor-pointer text-blue-500 hover:text-blue-700" style={{ fontSize: '1.1rem' }} title={th('brand.editTooltip')}></i>
            <i className="pi pi-history cursor-pointer text-blue-500 hover:text-blue-700" style={{ fontSize: '1.1rem' }} title={th('warrantyClaims.tooltips.history')}></i>
        </div>
    );

    return (
        <div className="card">
            <Toast ref={toast} />

            <div className="flex justify-content-between align-items-center mb-4">
                <h1 className="m-0">{th('brand.title')}</h1>
            </div>

            <div className="generic-table-filter mb-3">
                <div className="grid">
                    <div className="col-12 md:col-6 flex justify-content-start">
                        <Button icon="pi pi-filter" label={th('filter.advancedFilter')} onClick={toggleFilter} outlined={!filterOpen} />
                    </div>
                </div>

                {filterOpen && (
                    <div className="mt-3">
                        <Divider />
                        <div className="text-xl font-medium mb-3">{th('filter.advancedFilters')}</div>

                        <div className="flex flex-wrap gap-2" style={{ alignItems: 'end' }}>
                            <div className="flex-auto" style={{ minWidth: '240px', maxWidth: '300px' }}>
                                <div className="field mb-2 p-1">
                                    <label className="block mb-1 text-sm">{th('brand.brandName')}</label>
                                    <BrandDropdown value={brandSelect} onChange={(val) => setBrandSelect(val)} placeholder={th('filter.dropdownPlaceholder', { field: th('brand.brandName') })} className="w-full" />
                                </div>
                            </div>

                            <div className="flex-auto" style={{ minWidth: '240px', maxWidth: '300px' }}>
                                <div className="field mb-2 p-1">
                                    <label className="block mb-1 text-sm">{th('brand.brandShortName')}</label>
                                    <InputText value={brandShortText} onChange={(e) => setBrandShortText(e.target.value)} placeholder={th('filter.inputPlaceholder', { field: th('brand.brandShortName') })} className="w-full" />
                                </div>
                            </div>

                            <div className="flex-auto" style={{ minWidth: '240px', maxWidth: '300px' }}>
                                <div className="field mb-2 p-1">
                                    <label className="block mb-1 text-sm">{th('brand.brandForParts')}</label>
                                    <InputText value={brandItemValText} onChange={(e) => setBrandItemValText(e.target.value)} placeholder={th('filter.inputPlaceholder', { field: th('brand.brandForParts') })} className="w-full" />
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-content-start mt-3">
                            <Button label={th('filter.reset')} icon="pi pi-refresh" className="p-button-outlined mr-2" onClick={handleResetFilters} />
                            <Button label={th('filter.apply')} icon="pi pi-check" onClick={handleApplyFilters} />
                        </div>
                    </div>
                )}
            </div>

            <Dialog visible={dialogVisible} style={{ width: '450px' }} header={th('brand.editTitle')} modal className="p-fluid" footer={renderDialogFooter()} onHide={hideDialog}>
                <div className="field mb-4">
                    <label htmlFor="brandTitle" className="font-bold block mb-2">
                        {th('brand.brandName')} *
                    </label>
                    <InputText id="brandTitle" value={brandTitle} onChange={(e) => setBrandTitle(e.target.value)} className={submitted && !brandTitle.trim() ? 'p-invalid' : ''} placeholder={th('brand.brandNamePlaceholder')} />
                    {submitted && !brandTitle.trim() && <small className="p-error">{th('brand.brandNameRequired')}</small>}
                </div>

                <div className="field mb-4">
                    <label htmlFor="brandCode" className="font-bold block mb-2">
                        {th('brand.brandShortName')} *
                    </label>
                    <InputText id="brandCode" value={brandCode} onChange={(e) => setBrandCode(e.target.value)} className={submitted && !brandCode.trim() ? 'p-invalid' : ''} placeholder={th('brand.brandShortPlaceholder')} />
                    {submitted && !brandCode.trim() && <small className="p-error">{th('brand.brandShortRequired')}</small>}
                </div>

                <div className="field mb-4">
                    <label htmlFor="brandItemVal" className="font-bold block mb-2">
                        {th('brand.brandForParts')}
                    </label>
                    <InputText id="brandItemVal" value={brandItemVal} onChange={(e) => setBrandItemVal(e.target.value)} placeholder={th('brand.brandPartsPlaceholder')} />
                </div>
            </Dialog>

            <DataTable
                value={sortedData}
                lazy
                paginator
                first={lazyParams.first}
                rows={lazyParams.rows}
                totalRecords={totalRecords}
                onPage={onPage}
                onSort={onSort}
                sortField={lazyParams.sortField}
                sortOrder={lazyParams.sortOrder}
                className="p-datatable-gridlines"
                showGridlines
                dataKey="id"
                loading={loading}
                emptyMessage={th('table.noBrandsFound')}
                rowsPerPageOptions={[5, 10, 25, 50]}
                tableStyle={{ minWidth: '50rem' }}
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                currentPageReportTemplate={th('table.paginatorTemplate')}
            >
                <Column field="brand" header={th('table.brandName')} sortable style={{ width: '30%' }} />
                <Column field="brandShort" header={th('table.brandShortName')} sortable style={{ width: '30%' }} />
                <Column field="brandItemVal" header={th('table.brandForParts')} sortable style={{ width: '30%' }} />
            </DataTable>
        </div>
    );
};

export default BrandPage;
